import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { useToastNotificationContext } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useTranslation } from 'react-i18next';
import { reportErrorToFaro } from '@sendible/common';
import { defaultColor } from './components/NewCampaignModal/components';
import { DeleteModal } from './components';
import { pages } from '../../routes';
import { useCampaignsModel } from '../../models/campaigns';

type CampaignsContextType = {
  activeCampaignObject: CampaignsData;
  campaignsList: CampaignsData[];
  campaignsError: string | null;
  overviewError: string | null;
  isCampaignsLoading: boolean;
  isCampaignSaving: boolean;
  isEditionModalOpen: number | boolean;
  isSidebarVisible: boolean;
  saveCampaignFromObject: (campaign: CampaignsData) => void;
  setActiveCampaignObject: Dispatch<SetStateAction<CampaignsData>>;
  setIsEditionModalOpen: Dispatch<SetStateAction<number | boolean>>;
  setIsSidebarVisible: Dispatch<SetStateAction<boolean>>;
  overviewData?: CampaignsOverview;
  isOverviewLoading: boolean;
  setDeleteModalId: Dispatch<SetStateAction<number | null>>;
};

export const CampaignsContext = createContext({} as CampaignsContextType);

export const API_DATE_FORMAT = 'yyyy-MM-dd';
export const LONG_API_DATE_FORMAT = 'yyyy/MM/dd HH:mm:ss ZZZ';

const defaultActiveCampaign: CampaignsData = {
  id: -1,
  name: '',
  startDate: null,
  endDate: null,
  status: 'active',
  color: defaultColor,
};

const getLuxonDateFromApiDate = (dateString: string | null) => {
  return !dateString ? null : DateTime.fromFormat(dateString, LONG_API_DATE_FORMAT);
};

export const updateResultsToUseLuxonDate = (results: CampaignsDataFromApi[]) => {
  return results.map((campaignObject) => {
    return {
      ...campaignObject,
      startDate: getLuxonDateFromApiDate(campaignObject.startDate),
      endDate: getLuxonDateFromApiDate(campaignObject.endDate),
    };
  });
};

export const CampaignsProvider = ({ children }: Component) => {
  const { id: paramIdString } = useParams();
  const { t } = useTranslation('campaigns');
  const paramId = paramIdString ? parseInt(paramIdString, 10) : null;
  const [
    {
      user: { username, isWhiteLabel },
    },
  ] = useBridgeContext();
  const { showToastNotification } = useToastNotificationContext();

  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  const [isCampaignsLoading, setIsCampaignsLoading] = useState(true);
  const [isOverviewLoading, setIsOverviewLoading] = useState(false);

  const [isCampaignSaving, setIsCampaignSaving] = useState(false);
  const [isEditionModalOpen, setIsEditionModalOpen] = useState<number | boolean>(false);
  const [campaignsList, setCampaignsList] = useState<CampaignsData[]>([]);

  const [manualActiveCampaign, setManualActiveCampaign] = useState<CampaignsData | null>(null);

  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [deleteModalId, setDeleteModalId] = useState<number | null>(null);

  const [rawOverviewData, setRawOverviewData] = useState<CampaignsOverview | undefined>();
  const { getCampaignOverview, getCampaigns, saveCampaign, deleteCampaign } = useCampaignsModel();
  const navigate = useNavigate();

  const getCampaignFromId = (campaignsArray: CampaignsData[], targetId: number | null) => {
    return campaignsArray.find((campaignObject) => targetId && campaignObject.id === targetId);
  };

  const activeCampaignObject = useMemo((): CampaignsData => {
    if (manualActiveCampaign && (!paramId || manualActiveCampaign.id === paramId)) {
      return manualActiveCampaign;
    }
    if (paramId) {
      const match = getCampaignFromId(campaignsList, paramId);

      if (match) return match;
    }

    return campaignsList.length > 0 ? campaignsList[0] : defaultActiveCampaign;
  }, [campaignsList, paramId, manualActiveCampaign]);

  // React 19 Best Practice: Derive context overviewData dynamically during rendering
  // so that resetting it doesn't require a synchronous state dispatch loop.
  const overviewData = useMemo(() => {
    return paramId ? rawOverviewData : undefined;
  }, [paramId, rawOverviewData]);

  // Effect 1: Initial Context Initialization
  useEffect(() => {
    let isCurrentFetch = true;

    const getAndSetCampaigns = async (currentTargetId: number | null) => {
      try {
        const results = await getCampaigns();

        if (!isCurrentFetch) return;

        const resultsWithLuxonDate = updateResultsToUseLuxonDate(results);

        setCampaignsList(resultsWithLuxonDate);
        setManualActiveCampaign(null);

        const currentMatch = getCampaignFromId(resultsWithLuxonDate, currentTargetId);

        if (!currentMatch && resultsWithLuxonDate.length > 0) {
          navigate(`${pages.Campaigns.base}/${resultsWithLuxonDate[0].id}`);
        } else if (resultsWithLuxonDate.length === 0) {
          navigate(pages.Campaigns.base);
        }
      } catch (errorObject) {
        if (errorObject instanceof Error && isCurrentFetch) {
          reportErrorToFaro(errorObject, 'CampaignsContext getAndSetCampaigns');
          setCampaignsError(errorObject.message);
        }
      } finally {
        if (isCurrentFetch) {
          setIsCampaignsLoading(false);
        }
      }
    };

    getAndSetCampaigns(paramId);

    return () => {
      isCurrentFetch = false;
    };
  }, [username]);

  // Effect 2: Independent summary loading loop (Fully safe from synchronous state updates)
  useEffect(() => {
    if (!paramId) return;

    let isCurrentFetch = true;

    // Defer the loader flag state trigger to a microtask
    // to keep it isolated from the structural layout commit lifecycle phase.
    queueMicrotask(() => {
      if (isCurrentFetch) {
        setIsOverviewLoading(true);
      }
    });

    const getCampaignOverviewData = async (id: number) => {
      try {
        const result = await getCampaignOverview(id);

        if (isCurrentFetch) {
          setRawOverviewData(result);
          setOverviewError(null);
        }
      } catch (errorObject) {
        if (errorObject instanceof Error && isCurrentFetch) {
          reportErrorToFaro(errorObject, 'CampaignsContext getCampaignOverviewData');
          setOverviewError(errorObject.message);
        }
      } finally {
        if (isCurrentFetch) {
          setIsOverviewLoading(false);
        }
      }
    };

    getCampaignOverviewData(paramId);

    return () => {
      isCurrentFetch = false;
    };
  }, [paramId]);

  // Imperative callback mechanism for data mutation sequences
  const refreshCampaignsData = async (currentTargetId: number | null) => {
    setIsCampaignsLoading(true);
    try {
      const results = await getCampaigns();
      const resultsWithLuxonDate = updateResultsToUseLuxonDate(results);

      setCampaignsList(resultsWithLuxonDate);
      setManualActiveCampaign(null);

      const currentMatch = getCampaignFromId(resultsWithLuxonDate, currentTargetId);

      if (!currentMatch && resultsWithLuxonDate.length > 0) {
        navigate(`${pages.Campaigns.base}/${resultsWithLuxonDate[0].id}`);
      } else if (resultsWithLuxonDate.length === 0) {
        navigate(pages.Campaigns.base);
      }
    } catch (errorObject) {
      if (errorObject instanceof Error) {
        reportErrorToFaro(errorObject, 'CampaignsContext refreshCampaignsData');
        setCampaignsError(errorObject.message);
      }
    } finally {
      setIsCampaignsLoading(false);
    }
  };

  const saveCampaignFromObject = async (campaign: CampaignsData) => {
    try {
      const { color, name, startDate, endDate, status } = campaign;

      setIsCampaignSaving(true);

      const activeCampaignObjectForApi = {
        ...(color && { color }),
        ...(name && { name }),
        ...(startDate && { startDate: startDate.toFormat(API_DATE_FORMAT) }),
        ...(endDate && { endDate: endDate.toFormat(API_DATE_FORMAT) }),
        ...(paramId && { campaignId: paramId }),
        ...(status && { status }),
      };

      const result = await saveCampaign(activeCampaignObjectForApi, campaign.id ? 'PUT' : 'POST');

      setIsCampaignSaving(false);

      const targetId = campaign.id ? paramId : result.id;

      await refreshCampaignsData(targetId);

      if (!campaign.id) {
        navigate(`${pages.Campaigns.base}/${result.id}`);
      }
      setIsEditionModalOpen(false);
    } catch (errorObject) {
      reportErrorToFaro(errorObject, 'CampaignsContext saveCampaignFromObject');
      showToastNotification({
        icon: true,
        notificationText: isWhiteLabel ? t('error_saving_wl') || '' : t('error_saving') || '',
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });
      setIsCampaignSaving(false);
    }
  };

  const deleteCampaignById = async (campaignId?: number) => {
    try {
      if (!campaignId) return;
      await deleteCampaign(campaignId);
      await refreshCampaignsData(null);
    } catch (error: unknown) {
      reportErrorToFaro(error, 'CampaignsContext deleteCampaignById');
      showToastNotification({
        icon: true,
        notificationText: isWhiteLabel ? t('error_deleting_wl') || '' : t('error_deleting') || '',
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });
    }
  };

  return (
    <CampaignsContext.Provider
      value={{
        activeCampaignObject,
        campaignsList,
        campaignsError,
        overviewError,
        isCampaignsLoading,
        isCampaignSaving,
        isEditionModalOpen,
        isSidebarVisible,
        saveCampaignFromObject,
        setActiveCampaignObject: setManualActiveCampaign as Dispatch<SetStateAction<CampaignsData>>,
        setIsEditionModalOpen,
        setIsSidebarVisible,
        overviewData,
        isOverviewLoading,
        setDeleteModalId,
      }}
    >
      <AnimatePresence>
        {deleteModalId && (
          <DeleteModal
            closeModalCallback={() => {
              setDeleteModalId(null);
            }}
            deleteCallback={() => {
              deleteCampaignById(deleteModalId || undefined);
              setDeleteModalId(null);
            }}
          />
        )}
      </AnimatePresence>
      {children}
    </CampaignsContext.Provider>
  );
};

export const useCampaignsContext = (): CampaignsContextType => useContext(CampaignsContext);
